import { Component, inject } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { CommonModule } from '@angular/common';
import { MatDialogModule } from '@angular/material/dialog';
import { MatStepperModule } from '@angular/material/stepper';
import { MatButtonModule } from '@angular/material/button';
import { FormBuilder, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { FlagsPipe } from '../../../core/pipes/FlagClassPipe.pipe';
import { MatChipsModule } from '@angular/material/chips';
import { MatRadioModule } from '@angular/material/radio';
import { AnimationOptions } from 'ngx-lottie';
import { LottieComponent } from 'ngx-lottie';
import { MatIconModule } from '@angular/material/icon';
import { AppOptionsService } from '../../../core/services/app-options.service';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { UserProfileRequest } from '@core/model/userProfileRequest';
import { LanguageService } from '../../../core/services/language/language.service';
import { CustomCompanyService } from '../../../core/services/company.service';
import { CompaniesService } from '../../../core';
import { UserProfileService } from '../../../core/services/user-profile.service';
import { Router } from '@angular/router';

@Component({
  selector: 'app-profile-stepper-dialog',
  standalone: true,
  imports: [
    CommonModule,
    MatDialogModule,
    MatStepperModule,
    MatButtonModule,
    FormsModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    FlagsPipe,
    MatChipsModule,
    MatRadioModule,
    LottieComponent,
    MatIconModule,
    TranslateModule
  ],
  templateUrl: './profile-stepper-dialog.component.html',
  styleUrls: ['./profile-stepper-dialog.component.scss']
})
export class ProfileStepperDialogComponent {

  options: AnimationOptions = {
    path: '/assets/animations/welcome.json',
  };

  step = 0;
  private _formBuilder = inject(FormBuilder);
  private languageService = inject(LanguageService);
  private userProfileService = inject(UserProfileService);
  private router = inject(Router);
  isLinear = true;
  languages: { code: string, name: string }[] = [];
  companyActiveLanguages: { code: string, name: string, translatedName: string }[] = [];
  profiles: { name: string, code: number }[] = [];
  targets: { value: string, title: string, description: string }[] = [];

  constructor(
    public dialogRef: MatDialogRef<ProfileStepperDialogComponent>,
    private optionsService: AppOptionsService,
    public translate: TranslateService,
    private companyService: CustomCompanyService,
    private companiesService: CompaniesService
  ) {

    this.thirdFormGroup.get('profile')?.valueChanges.subscribe(profile => {
      if (profile == 0) {
        this.thirdFormGroup.get('newProfile')?.setValidators([Validators.required]);
      } else {
        this.thirdFormGroup.get('newProfile')?.setValidators([Validators.nullValidator]);
        this.thirdFormGroup.get('newProfile')?.setValue('');
      }
      this.thirdFormGroup.get('newProfile')?.updateValueAndValidity();
    });

    this.fourthFormGroup.get('gonnaWorkWithProductsData')?.valueChanges.subscribe(gonnaWorkWithProductsData => {
      if (gonnaWorkWithProductsData == 'false') {
        this.fourthFormGroup.get('language')?.setValue('');
        this.thirdFormGroup.get('newProfile')?.updateValueAndValidity();
      }
    });

  }

  ngOnInit() {

    this.optionsService.getLanguages().subscribe(languages => {
      this.languages = languages;
      this.companyActiveLanguages = this.companyService.getActiveLanguages();
      this.companyActiveLanguages = this.companyActiveLanguages.map(lang => ({
        ...lang,
        translatedName: this.languages.find(l => l.code === lang.code)?.name || lang.name
      }));
      const currentLangNavCode = this.languageService.getLanguageCode();
      const langExists = this.languages.some(l => l.code === currentLangNavCode);
      this.switchLanguage(langExists ? currentLangNavCode : 'en');
    });
    this.profiles = this.optionsService.getProfiles();
    this.targets = [
      {
        value: 'products-upload',
        title: 'STANDALONE-COMPONENTS.PROFILE-STEPPER.STEP_5.TARGETS.PRODUCTS-UPLOAD.TITLE',
        description: 'STANDALONE-COMPONENTS.PROFILE-STEPPER.STEP_5.TARGETS.PRODUCTS-UPLOAD.DESCRIPTION'
      },
      {
        value: 'products-catalog',
        title: 'STANDALONE-COMPONENTS.PROFILE-STEPPER.STEP_5.TARGETS.PRODUCTS-CATALOG.TITLE',
        description: 'STANDALONE-COMPONENTS.PROFILE-STEPPER.STEP_5.TARGETS.PRODUCTS-CATALOG.DESCRIPTION'
      },
      {
        value: 'providers',
        title: 'STANDALONE-COMPONENTS.PROFILE-STEPPER.STEP_5.TARGETS.PROVIDERS.TITLE',
        description: 'STANDALONE-COMPONENTS.PROFILE-STEPPER.STEP_5.TARGETS.PROVIDERS.DESCRIPTION'
      },
      {
        value: 'home',
        title: 'STANDALONE-COMPONENTS.PROFILE-STEPPER.STEP_5.TARGETS.HOME.TITLE',
        description: 'STANDALONE-COMPONENTS.PROFILE-STEPPER.STEP_5.TARGETS.HOME.DESCRIPTION'
      }
    ];
  }

  firstFormGroup = this._formBuilder.group({
    language: ['', Validators.required],
  });
  secondFormGroup = this._formBuilder.group({
    name: ['', Validators.required],
  });
  thirdFormGroup = this._formBuilder.group({
    profile: [1, Validators.required],
    newProfile: [''],
  });
  fourthFormGroup = this._formBuilder.group({
    gonnaWorkWithProductsData: ['true', Validators.required],
    language: [''],
  });
  fifthFormGroup = this._formBuilder.group({
    target: ['', Validators.required],
  });

  switchLanguage(languageCode: string) {
    this.translate.use(languageCode);
  }

  save() {
    const name = this.secondFormGroup.get('name')?.value || '';
    const language = this.firstFormGroup.get('language')?.value || '';
    const profileCode = this.thirdFormGroup.get('profile')?.value || 1;
    const newProfile = this.thirdFormGroup.get('newProfile')?.value || '';
    const defaultProductLanguage = this.fourthFormGroup.get('language')?.value || '';
    
    const foundProfileForAvatar = this.profiles.find(p => p.code === profileCode);
    const profileName = foundProfileForAvatar ? foundProfileForAvatar.name.split('.').pop()?.toLowerCase() : '';
    const avatar = profileName ? `/img/profiles/${profileName}.png` : '';

    let profile: any = undefined;
    if (newProfile) {
      profile = { code: 0, name: newProfile };
    } else {
      const foundProfile = this.profiles.find(r => r.code === profileCode);
      if (foundProfile) {
        profile = { code: foundProfile.code, name: this.translate.instant(foundProfile.name) };
      }
    }

    const userProfileRequest: UserProfileRequest = {
      name: name,
      language: language,
      profile: profile.code,
      defaultProductLanguage: defaultProductLanguage,
      email: '',
      phoneNumber: '',
      surname: '',
      country: '',
      avatar: avatar,
      enabledLanguages: undefined,
      profileName: profile.name || ''
    };

    this.userProfileService.markUserAsOnboarded(userProfileRequest).subscribe({
      next: () => {
        this.userProfileService.getUserProfile().subscribe({
          next: (profile) => {
            profile.enabledLanguages = this.companyService.getLanguages();
            this.userProfileService.setProfile(profile);
            this.dialogRef.close();
            this.router.navigate(['/home']);
          },
          error: err => {
            console.error('Error al obtener el perfil', err);
          }
        });
      },
      error: err => {
        console.error('Error al guardar el perfil', err);
      }
    });
  }

}

